import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock,
  Facebook,
  Instagram,
  Youtube,
  Linkedin
} from 'lucide-react';

const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-primary text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <div className="flex items-center space-x-2 mb-6">
              <div className="w-12 h-12 bg-gold rounded-lg flex items-center justify-center">
                <span className="text-primary font-bold text-xl">KDS</span>
              </div>
              <div>
                <div className="font-bold text-lg">Karni Digital</div>
                <div className="text-sm text-gold">Solutions</div>
              </div>
            </div>
            <p className="text-gray-300 mb-6">
              {t('footer.tagline')}
            </p>
            <div className="flex space-x-4">
              <a href="#" className="text-white hover:text-gold transition-colors">
                <Facebook size={24} />
              </a>
              <a href="#" className="text-white hover:text-gold transition-colors">
                <Instagram size={24} />
              </a>
              <a href="#" className="text-white hover:text-gold transition-colors">
                <Youtube size={24} />
              </a>
              <a href="#" className="text-white hover:text-gold transition-colors">
                <Linkedin size={24} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-bold text-lg mb-6 text-gold">{t('footer.quick_links')}</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-gray-300 hover:text-gold transition-colors">
                  {t('nav.home')}
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-300 hover:text-gold transition-colors">
                  {t('nav.about')}
                </Link>
              </li>
              <li>
                <Link to="/portfolio" className="text-gray-300 hover:text-gold transition-colors">
                  {t('nav.portfolio')}
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-gray-300 hover:text-gold transition-colors">
                  {t('nav.pricing')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-bold text-lg mb-6 text-gold">{t('footer.services_links')}</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/services" className="text-gray-300 hover:text-gold transition-colors">
                  {t('services.web_development')}
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-gray-300 hover:text-gold transition-colors">
                  {t('services.mobile_apps')}
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-gray-300 hover:text-gold transition-colors">
                  {t('services.digital_marketing')}
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-gray-300 hover:text-gold transition-colors">
                  {t('services.real_estate')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-bold text-lg mb-6 text-gold">{t('footer.contact_info')}</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-gold mt-1 flex-shrink-0" />
                <div className="text-gray-300">
                  <div>{t('contact.address')}</div>
                  <div>{t('contact.pattaya_address')}</div>
                </div>
              </li>
              <li className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-gold flex-shrink-0" />
                <a 
                  href="tel:+66968826252" 
                  className="text-gray-300 hover:text-gold transition-colors"
                >
                  +66 968826252
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-gold flex-shrink-0" />
                <a 
                  href="mailto:mail@karnidigitalsolutions.com" 
                  className="text-gray-300 hover:text-gold transition-colors"
                >
                  mail@karnidigitalsolutions.com
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Clock className="w-5 h-5 text-gold mt-1 flex-shrink-0" />
                <div className="text-gray-300">
                  <div>{t('contact.hours')}</div>
                  <div>{t('contact.hours_detail')}</div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* QR Code Section */}
        <div className="border-t border-gold border-opacity-30 mt-8 sm:mt-12 pt-6 sm:pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="mb-4 md:mb-0 text-center md:text-left">
              <h4 className="font-semibold text-gold mb-2 text-sm sm:text-base">Scan QR Code to Visit Our Website</h4>
              <a href="https://karnidigitalsolutions.com" target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-lg inline-block">
                <img 
                  src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https%3A%2F%2Fkarnidigitalsolutions.com" 
                  alt="QR code to open Karni Digital Solutions website"
                  className="w-24 h-24"
                  loading="lazy"
                />
              </a>
            </div>
            
            {/* Google Map Embed */}
            <div className="text-center w-full md:w-auto">
              <h4 className="font-semibold text-gold mb-2 text-sm sm:text-base">Find Us in Pattaya</h4>
              <div className="w-full sm:w-72 h-40 sm:h-40 rounded-lg overflow-hidden">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d62221.1365412773!2d100.8199796959312!3d12.919221372057988!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x310297343e656c75%3A0x12cf0b53746d9dff!2sKarni%20Digital%20Solutions%20%E2%80%93%20Web%20%26%20App%20Development%20Pattaya!5e0!3m2!1sen!2sth!4v1763642213122!5m2!1sen!2sth" 
                  width="100%" 
                  height="100%" 
                  style={{ border: 0 }} 
                  allowFullScreen 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Karni Digital Solutions Location (Footer)"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-gold border-opacity-30 mt-6 sm:mt-8 pt-6 sm:pt-8 text-center">
          <p className="text-gray-300 text-sm sm:text-base">© {currentYear} Karni Digital Solutions. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
